import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchAuthSession } from "@/authSessionClient";

type LocationState = { sessionId?: string; delivery?: "phone" | "email" };

export function VerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loc = location.state as LocationState | null;
  const sessionId = loc?.sessionId;
  const delivery = loc?.delivery ?? "phone";

  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAuthSession()
      .then((r) => r.json())
      .then((d: { authenticated?: boolean }) => {
        if (d.authenticated) navigate("/", { replace: true });
      })
      .catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { replace: true });
    }
  }, [sessionId, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionId) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/verify-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ sessionId, code: code.trim() }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Verification failed");
        return;
      }
      navigate("/", { replace: true });
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!sessionId) {
    return null;
  }

  return (
    <div className="min-h-screen text-white flex flex-col bg-black">
      <div className="flex shrink-0 justify-center px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-6">
        <img
          src="/assets/kennedy-logo.webp"
          alt="Kennedy Ryon"
          className="max-w-[120px] h-auto"
        />
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <img
              src="/assets/evolve.jpeg"
              alt="Can I Evolve? album art"
              className="w-40 mx-auto mb-4 rounded-lg shadow-xl"
            />
            <h1 className="text-2xl font-bold">Enter code to access album</h1>
            <p className="text-gray-400 text-sm mt-2">
              {delivery === "email"
                ? "We sent a 6-digit code to your email."
                : "We sent a 6-digit code to your phone."}
            </p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm text-gray-400 mb-1">
                Code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white text-center tracking-widest font-mono text-lg placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="______"
                required
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={submitting || code.trim().length < 6}
              className={
                "w-full mt-1.5 inline-flex items-center justify-center gap-2 rounded-[10px] border-0 px-4 py-[15px] " +
                "bg-[#9EBB89] text-base font-bold text-black " +
                "transition-[transform,box-shadow,background-color,opacity] duration-[400ms] ease-in-out " +
                "hover:-translate-y-0.5 hover:bg-[#C7D9B7] hover:shadow-[0_10px_36px_rgba(199,217,183,0.25)] " +
                "disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:bg-[#9EBB89] disabled:hover:shadow-none"
              }
            >
              {submitting ? "Verifying…" : "Verify"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
